import { buttonFragment } from '../../graphql/fragments/buttonFragment';
import { dynamicContentFragment } from '../../graphql/fragments/dynamicContentFragment';
import { followPropertyModalFragment } from '../../graphql/fragments/followPropertyModalFragment';
import { weblinkFragment } from '../../graphql/fragments/weblinkFragment';
import { graphql } from '../../graphql/graphql';

const beforeYouBuyModalFragment = graphql(
  `
    fragment PropertyPage_BeforeYouBuyModal on PropertyPage_Page {
      beforeYouBuyModal {
        title
        propertyAddress
        priceBlock {
          priceTitle
          price
          fullCost
          disclaimer
          disclaimerV2 @skip(if: $toggleBYBLegalDisclaimers) {
            ...DynamicContent
          }
          disclaimerV3 @include(if: $toggleBYBLegalDisclaimers) {
            ...DynamicContent
          }
        }
        eta @skip(if: $toggleBYBLegalDisclaimers)
        etaV2 @include(if: $toggleBYBLegalDisclaimers)
        featureTitle
        featureList @skip(if: $toggleBYBLegalDisclaimers)
        featureListV2 @include(if: $toggleBYBLegalDisclaimers) {
          ...DynamicContent
        }
        beforeYouBuyLink {
          ...WebLink
        }
      }
    }
  `,
  [weblinkFragment, dynamicContentFragment],
);

const propertyPageHelmetFragment = graphql(`
  fragment PropertyPage_Helmet on PropertyPage_Page {
    helmet {
      canonical
      title
      description
      siteName
      seoSchemas
      ogImage(width: 1200, height: 630, format: JPG) {
        url
        alt
        secure_url
        width
        height
        type
      }
      resourceHints {
        rel
        href
        as
        type
        crossorigin
      }
    }
  }
`);

export const propertyPageMediaShowcaseV2ListingItemFragment = graphql(
  `
    fragment PropertyPage_MediaShowcaseV2_ListingItem on PropertyPage_MediaShowcaseV2_ListingItem {
      __typename
      key
      image {
        alt
        mobileFullScreenImageJpgUri: uri(
          width: 569
          height: 368
          imageMode: Crop
          progressive: true
        )
        mobileFullScreenImageWebpUri: uri(
          width: 569
          height: 368
          format: WEBP
          imageMode: Crop
          progressive: true
        )
        desktopFullScreenImageJpgUri: uri(
          width: 850
          height: 352
          imageMode: Crop
          progressive: true
        )
        desktopFullScreenImageWebpUri: uri(
          width: 850
          height: 352
          format: WEBP
          imageMode: Crop
          progressive: true
        )
      }
      searchByImageButton {
        ...WebLink
      }
    }
  `,
  [weblinkFragment],
);

export const propertyPageMediaShowcaseV2AerialItemFragment = graphql(`
  fragment PropertyPage_MediaShowcaseV2_AerialItem on PropertyPage_MediaShowcaseV2_AerialItem {
    __typename
    key
    image {
      alt
      uri(width: FULL)
    }
  }
`);

export const propertyPageMediaShowcaseV2SplitItemsFragment = graphql(
  `
    fragment PropertyPage_MediaShowcaseV2_SplitItems on PropertyPage_MediaShowcaseV2_SplitItems {
      __typename
      main {
        ...PropertyPage_MediaShowcaseV2_ListingItem
      }
      secondary {
        ... on PropertyPage_MediaShowcaseV2_ListingItem {
          ...PropertyPage_MediaShowcaseV2_ListingItem
        }
        ... on PropertyPage_MediaShowcaseV2_AerialItem {
          ...PropertyPage_MediaShowcaseV2_AerialItem
        }
      }
      tertiary {
        ...PropertyPage_MediaShowcaseV2_ListingItem
      }
    }
  `,
  [propertyPageMediaShowcaseV2ListingItemFragment, propertyPageMediaShowcaseV2AerialItemFragment],
);

export const propertyPageMediaShowcaseV2Fragment = graphql(
  `
    fragment PropertyPage_MediaShowcaseV2 on PropertyPage_Page {
      mediaShowcaseV2 {
        items {
          ... on PropertyPage_MediaShowcaseV2_SplitItems {
            ...PropertyPage_MediaShowcaseV2_SplitItems
          }
          ... on PropertyPage_MediaShowcaseV2_ListingItem {
            ...PropertyPage_MediaShowcaseV2_ListingItem
          }
          ... on PropertyPage_MediaShowcaseV2_AerialItem {
            ...PropertyPage_MediaShowcaseV2_AerialItem
          }
        }
        mediaCounts {
          iconNameMd
          title
          value
          imageIndexFrom
        }
      }
    }
  `,
  [
    propertyPageMediaShowcaseV2SplitItemsFragment,
    propertyPageMediaShowcaseV2ListingItemFragment,
    propertyPageMediaShowcaseV2AerialItemFragment,
  ],
);

export const propertyPageMediaGalleryV2Fragment = graphql(
  `
    fragment PropertyPage_MediaGalleryV2 on PropertyPage_Page {
      mediaGalleryV2 {
        items {
          key
          image {
            ... on PropertyPage_ListingImage {
              ...PropertyPage_MediaGalleryV2_ListingImage
            }
            ... on PropertyPage_AerialImage {
              ...PropertyPage_MediaGalleryV2_AerialImage
            }
          }
          tracking {
            fullScreenCarouselView {
              schema
              data {
                event_name
                property_id
                media {
                  url
                  index
                  type
                }
              }
            }
          }
          searchByImageButton {
            ...WebLink
          }
        }
        mediaCounts {
          iconNameMd
          title
          value
          imageIndexFrom
        }
        timeline {
          year
          indexFrom
          indexTo
        }
        mobileAd: advertisement {
          variant
        }
        desktopAd: advertisement {
          variant
        }
      }
    }

    fragment PropertyPage_MediaGalleryV2_ListingImage on PropertyPage_ListingImage {
      alt
      captionV2
      mobileFullScreenImageJpgUri: uri(width: 1200, height: 900, imageMode: Fit, progressive: true)
      mobileFullScreenImageWebpUri: uri(
        width: 1200
        height: 900
        format: WEBP
        imageMode: Fit
        progressive: true
      )
      desktopFullScreenImageJpgUri: uri(
        width: 2560
        height: 1920
        imageMode: Fit
        progressive: true
      )
      desktopFullScreenImageWebpUri: uri(
        width: 2560
        height: 1920
        format: WEBP
        imageMode: Fit
        progressive: true
      )
    }

    fragment PropertyPage_MediaGalleryV2_AerialImage on PropertyPage_AerialImage {
      alt
      captionV2
      uri(width: FULL)
    }
  `,
  [weblinkFragment],
);

export const propertyPageValueEstimatesV2Fragment = graphql(`
  fragment PropertyPage_ValueEstimatesV2 on PropertyPage_Page {
    valueEstimatesV2 {
      estimates {
        sale {
          price(unit: AUD) @include(if: $toggleShowAVMData) {
            valueWithUnit
            value
          }
          confidence {
            type
            label
          }
          range @include(if: $toggleShowAVMData) {
            max {
              valueWithUnit
              value
            }
            maxTitle
            min {
              valueWithUnit
              value
            }
            minTitle
          }
          priceSubtitle @include(if: $toggleShowAVMData)
          title
          subtitle
        }
        rental {
          price(unit: AUD) @include(if: $toggleShowAVMData) {
            valueWithUnit
            value
          }
          confidence {
            type
            label
          }
          range @include(if: $toggleShowAVMData) {
            max {
              valueWithUnit
              value
            }
            maxTitle
            min {
              valueWithUnit
              value
            }
            minTitle
          }
          title
          subtitle
          emptyEstimateMessage
        }
      }
      title
      subtitle
      caption @include(if: $toggleShowAVMData)
      hideConfidenceLabel
      alert {
        text
        variant
      }
    }
  }
`);

export const propertyPagePropertyDescriptionFragment = graphql(
  `
    fragment PropertyPage_PropertyDescription on PropertyPage_Page {
      propertyDescriptionV2 {
        title
        description(showAVM: $toggleShowAVMData) {
          ...DynamicContent
        }
      }
    }
  `,
  [dynamicContentFragment],
);

export const propertyPageTimelineV4Fragment = graphql(
  `
    fragment PropertyPage_Timeline on PropertyPage_Page {
      timelineV4 {
        title
        subtitle
        eventGroups {
          label
          events {
            ...PropertyPage_TimelineEvent
          }
        }
        beforeYouBuy @skip(if: $toggleBYBLegalDisclaimers) {
          title
          subtitle
          disclaimer {
            ...DynamicContent
          }
          reportCTAV2 {
            text
            buttonVariant
            ariaLabel
            trackingEvent
            iconName
            buttonType
            iconPlacement
            sizeVariant
          }
        }

        beforeYouBuyV2 @include(if: $toggleBYBLegalDisclaimers) {
          title
          subtitle
          disclaimer {
            ...DynamicContent
          }
          reportCTA {
            text
            buttonVariant
            ariaLabel
            trackingEvent
            iconName
            buttonType
            iconPlacement
            sizeVariant
          }
        }
      }
    }

    fragment PropertyPage_TimelineEvent on PropertyPage_TimelineEvent {
      badgeV3 {
        text
        variant
      }
      titleV2
      tooltip
      subtitle
      details
      linksV2 {
        title
        url
        isExternal
        variant
        iconInfo {
          name
          placement
        }
        key
        tracking {
          outboundTracking {
            schema
            data {
              event_name
              type
              click_url
              name
              context {
                key
                value
              }
            }
          }
        }
      }
      media {
        key
        image {
          alt
          jpgUri: uri(width: 88, height: 56)
          webpUri: uri(width: 88, height: 56, format: WEBP)
        }
        mediaCounts {
          iconNameMd
          title
          value
        }
        caption
      }
      agency {
        brandColour
        logo {
          alt
          jpgUri: uri(width: 101, height: 19)
          webpUri: uri(width: 101, height: 19, format: WEBP)
        }
      }
      agent {
        photo {
          alt
          jpgUri: uri(width: 67, height: 90)
          webpUri: uri(width: 67, height: 90, format: WEBP)
        }
        name
        jobTitle
      }
    }
  `,
  [dynamicContentFragment],
);

export const propertyPagePropertyCollectionV3Fragment = graphql(`
  fragment PropertyPage_PropertyCollectionV3 on PropertyPage_Page {
    propertyCollectionsV3 {
      title
      subtitle
      properties {
        soldDate
        image {
          alt
          caption
          jpgUri: uri(width: 224, height: 126, imageMode: Crop, progressive: true)
          webpUri: uri(width: 224, height: 126, format: WEBP, imageMode: Crop, progressive: true)
        }
        imageV3 @include(if: $toggleMainImageDataMigration) {
          ... on PropertyPage_ListingImage {
            alt
            jpgUri: uri(width: 224, height: 126, imageMode: Crop, progressive: true)
            webpUri: uri(width: 224, height: 126, format: WEBP, imageMode: Crop, progressive: true)
          }
          ... on PropertyPage_AerialImage {
            alt
            uri(width: _420P)
          }
        }
        badgeV2 {
          text
          variant
        }
        priceV2 {
          display
          sourceLevel
        }
        address
        pathV2
        clickThroughTracking {
          pathname
          page
          element
          sourceType
        }
        features {
          iconNameV2
          title
          valueWithUnit
          hoverTitle
        }
      }
      tracking {
        showMoreProperties {
          schema
          data {
            event_name
            user_action
            in_page_source
          }
        }
      }
      searchPageWebLink {
        title
        url
        variant
        tracking {
          clickThroughTracking {
            pathname
            page
            element
            sourceType
          }
        }
        key
        isExternal
        iconInfo {
          name
          placement
        }
        disabled
      }
    }
  }
`);

const propertyPagePropertyCollectionV2Fragment = graphql(`
  fragment PropertyPage_PropertyCollectionV2 on PropertyPage_PropertyCollectionV2 {
    title
    subtitle
    properties {
      soldDate
      image {
        alt
        caption
        jpgUri: uri(width: 224, height: 126, imageMode: Crop, progressive: true)
        webpUri: uri(width: 224, height: 126, format: WEBP, imageMode: Crop, progressive: true)
      }
      imageV2 {
        ... on PropertyPage_ListingImage {
          alt
          jpgUri: uri(width: 224, height: 126, imageMode: Crop, progressive: true)
          webpUri: uri(width: 224, height: 126, format: WEBP, imageMode: Crop, progressive: true)
        }
        ... on PropertyPage_AerialImage {
          alt
          uri(width: _420P)
        }
      }
      imageV3 @include(if: $toggleMainImageDataMigration) {
        ... on PropertyPage_ListingImage {
          alt
          jpgUri: uri(width: 224, height: 126, imageMode: Crop, progressive: true)
          webpUri: uri(width: 224, height: 126, format: WEBP, imageMode: Crop, progressive: true)
        }
        ... on PropertyPage_AerialImage {
          alt
          uri(width: _420P)
        }
      }
      badgeV2 {
        text
        variant
      }
      priceV2 {
        display
        sourceLevel
      }
      address
      pathV2
      clickThroughTracking {
        pathname
        page
        element
        sourceType
      }
      features {
        iconNameV2
        title
        valueWithUnit
        hoverTitle
      }
    }
    tracking {
      showMoreProperties {
        schema
        data {
          event_name
          user_action
          in_page_source
        }
      }
    }
  }
`);

export const propertyPageSchoolCollectionFragment = graphql(
  `
    fragment PropertyPage_SchoolCollection on PropertyPage_Page {
      nearbySchools {
        title
        subtitle
        subtitleWithDynamicContent {
          ...DynamicContent
        }
        schoolFactHeader
        schools {
          schoolName
          isDefaultOpen
          isZoned
          schoolFacts {
            title
            subtitle
          }
          webLinks {
            title
            url
            isExternal
            key
            variant
            rel
            iconInfo {
              name
              placement
            }
            tracking {
              outboundTracking {
                schema
                data {
                  event_name
                  type
                  click_url
                  name
                  context {
                    key
                    value
                  }
                }
              }
              clickThroughTracking {
                pathname
                page
                element
                sourceType
              }
            }
          }
          tracking {
            expand {
              schema
              data {
                event_name
                in_page_source
                user_action
                accordion_copy
              }
            }
            collapse {
              schema
              data {
                event_name
                in_page_source
                user_action
                accordion_copy
              }
            }
          }
        }
      }
    }
  `,
  [dynamicContentFragment],
);

export const propertyPageChildcareCollectionFragment = graphql(`
  fragment PropertyPage_ChildcareCollection on PropertyPage_Page {
    nearbyChildcareCentres {
      title
      subtitle
      centreFactHeader
      centres {
        isDefaultOpen
        tracking {
          expand {
            schema
            data {
              event_name
              in_page_source
              user_action
              accordion_copy
            }
          }
          collapse {
            schema
            data {
              event_name
              in_page_source
              user_action
              accordion_copy
            }
          }
        }
        centre {
          name
          vacancyBadge {
            text
            variant
          }
          openingHours
          address
          webLinks {
            title
            url
            isExternal
            variant
            key
            tracking {
              outboundTracking {
                schema
                data {
                  event_name
                  type
                  click_url
                  name
                  context {
                    key
                    value
                  }
                }
              }
              clickThroughTracking {
                pathname
                page
                element
                sourceType
              }
            }
          }
          childcareFacts {
            ... on Fact {
              title
              subtitle
            }
            ... on StarRatingFact {
              displayStarRatingPercentage
              accessibilityTitle
              title
              subtitle
            }
          }
        }
      }
    }
  }
`);

export const propertyPagePropertyFeaturesBrickFragment = graphql(
  `
    fragment PropertyPage_PropertyFeaturesBrick on PropertyPage_Page {
      propertyFeaturesV2 {
        title
        subtitle
        keyFeatures {
          title
          subtitle
          itemsV2 {
            iconNameV2
            title
            hoverTitle
            valueWithUnit
          }
          updatePropertyDetails {
            cta {
              ... on PropertyPage_UpdatePropertyDetailsModalCta {
                button {
                  ...Button
                }
                modalEntryPoint
              }
            }
          }
        }
        additionalFeatures {
          title
          subtitle
          itemsV2 {
            iconNameV2
            title
            hoverTitle
            valueWithUnit
          }
        }
        energyEfficiency {
          title
          subtitle
          energyEfficiency
          description {
            ...DynamicContent
          }
        }
      }
    }
  `,
  [dynamicContentFragment, buttonFragment, weblinkFragment],
);

const propertyPagePropertyMap = graphql(`
  fragment PropertyPage_PropertyMap on PropertyPage_Page {
    propertyMap {
      title
      subtitle
      pins {
        coordinates {
          latitude
          longitude
        }
      }
      tracking {
        changeZoomLevel {
          schema
          data {
            event_name
            interaction_type
            in_page_source
          }
        }
        toggleFullscreen {
          schema
          data {
            event_name
            interaction_type
            in_page_source
          }
        }
      }
      propertyBoundary {
        type
        features {
          type
          geometry {
            type
            coordinates
          }
        }
      }
      propertyBoundaryMeasurements {
        label
        coordinates {
          latitude
          longitude
        }
      }
      propertyBoundaryLandAreas {
        label
        coordinates {
          latitude
          longitude
        }
      }
      propertyBoundaryVertices {
        latitude
        longitude
      }
      buildingBoundaries {
        type
        features {
          type
          geometry {
            type
            coordinates
          }
        }
      }
    }
  }
`);

const propertyPageInternetAvailabilityBrickFragment = graphql(
  `
    fragment PropertyPage_InternetAvailabilityBrick on PropertyPage_Page {
      internetAvailability @skip(if: $toggleShowOriginNbnBrick) {
        title
        subtitle
        description {
          ...DynamicContent
        }
        connectionQuality
        nbnConnection {
          title
          subtitle
          badgeV2 {
            text
            variant
          }
          description {
            ...DynamicContent
          }
        }
        mobileCoverage {
          title
          subtitle
          badgeV2 {
            text
            variant
          }
          description {
            ...DynamicContent
          }
        }
      }
    }
  `,
  [dynamicContentFragment],
);

const propertyPageInternetAvailabilityBrickV2Fragment = graphql(
  `
    fragment PropertyPage_InternetAvailabilityBrickV2 on PropertyPage_Page {
      internetAvailabilityV2 @include(if: $toggleShowOriginNbnBrick) {
        title
        subtitle
        nbnConnection {
          title
          subtitle
          badge {
            text
            variant
          }
          description {
            ...DynamicContent
          }
          connectionQuality
        }
        eligibleConnection {
          title
          subtitle
          badge {
            text
            variant
          }
          description {
            ...DynamicContent
          }
          connectionQuality
        }
        disclaimer
        originCTABanner {
          title
          subtitle
          ctaButton {
            key
            title
            url
            isExternal
            variant
            iconInfo {
              name
              placement
            }
            tracking {
              outboundTracking {
                schema
                data {
                  event_name
                  type
                  click_url
                  name
                  context {
                    key
                    value
                  }
                }
              }
            }
            rel
          }
          currentConnection
          upgradeAvailable
        }
      }
    }
  `,
  [dynamicContentFragment],
);

const propertyPageGovernmentDataBrickFragment = graphql(
  `
    fragment PropertyPage_GovernmentDataBrick on PropertyPage_Page {
      governmentDataV2 @skip(if: $toggleGovernmentOverlaysUpdate) {
        title
        subtitle
        landDescription {
          text
          bold
        }
        overlayTiles {
          title
          badge {
            text
            variant
          }
          stamp
          description {
            ...DynamicContent
          }
        }
        showMoreButton {
          title
        }
        overlaySummary {
          title
          overlayTypeSummaries {
            overlayType
            overlays
          }
          moreInformation {
            ...DynamicContent
          }
        }
        moreInformation {
          ...DynamicContent
        }
        partnerBanner {
          partnerBannerVariant
          isPropertyNorthernTerritory
          state
        }
      }
    }
  `,
  [dynamicContentFragment],
);

export const propertyPageGovernmentOverlaysBrickFragment = graphql(
  `
    fragment PropertyPage_GovernmentOverlaysBrick on PropertyPage_Page {
      governmentDataV3(toggleAllianzRenewalBannerV2: $toggleAllianzRenewalBannerV2)
        @include(if: $toggleGovernmentOverlaysUpdate) {
        title
        subtitle
        landDescription {
          text
          bold
        }
        overlayTiles {
          title
          badge {
            text
            variant
          }
          stamp
        }
        overlayDetails {
          title
          description {
            ...DynamicContent
          }
          overlayBreakdown {
            categoryTitle
            overlays
            tooltip
          }
        }
        moreInformation {
          ...DynamicContent
        }
        partnerBanner {
          partnerBannerVariant
          isPropertyNorthernTerritory
          state
        }
        partnerBannerV2 {
          bannerDetails {
            ... on PropertyPage_GovernmentDataPartnerBannerDualCTADetails {
              title
              description
              ctaHeadline
              link1 {
                ...WebLink
              }
              link2 {
                ...WebLink
              }
              disclaimer
              imageVariant
              impressionTracking {
                schema
                data {
                  event_name
                  user_action
                  type
                  name
                  context {
                    key
                    value
                  }
                }
              }
              link1Tracking {
                schema
                data {
                  event_name
                  user_action
                  type
                  name
                  context {
                    key
                    value
                  }
                }
              }
              link2Tracking {
                schema
                data {
                  event_name
                  user_action
                  type
                  name
                  context {
                    key
                    value
                  }
                }
              }
            }
            ... on PropertyPage_GovernmentDataPartnerBannerSingleCTADetails {
              title
              description
              link {
                ...WebLink
              }
              disclaimer
              imageVariant
              impressionTracking {
                schema
                data {
                  event_name
                  user_action
                  type
                  name
                  context {
                    key
                    value
                  }
                }
              }
              linkTracking {
                schema
                data {
                  event_name
                  user_action
                  type
                  name
                  context {
                    key
                    value
                  }
                }
              }
            }
          }
          isDualCTA
        }
        dataDisclaimers {
          geoscapeDisclaimerHtml
          archistarDisclaimerHtml
        }
      }
    }
  `,
  [dynamicContentFragment, weblinkFragment],
);

export const propertyPageNearbyPlanningPermitsBrickFragment = graphql(
  `
    fragment PropertyPage_NearbyPlanningPermitsBrick on PropertyPage_Page {
      nearbyPlanningPermits {
        title
        subtitle
        disclaimer {
          ...DynamicContent
        }
        tiles {
          title
          subtitle
          description
          badge {
            text
            variant
          }
          propertyPath
          clickThroughTracking {
            pathname
            page
            element
            sourceType
          }
        }
        showMoreButton {
          button {
            text
            buttonVariant
          }
          visibleOnMobile
          visibleOnDesktop
        }
        mobileVisibleTilesLimit
        desktopVisibleTilesLimit
      }
    }
  `,
  [dynamicContentFragment],
);

const propertyPageMarketInsightsBrickV3Fragment = graphql(
  `
    fragment PropertyPage_MarketInsightsBrickV3 on PropertyPage_Page {
      marketInsightsV3 {
        title
        subtitle
        medianPrice {
          title
          subtitle
          disclaimer
          cost {
            title
            suffix
          }
          costChange {
            title
            value
            iconNameV2
            variant
          }
          graph {
            points {
              date
              value
              hover {
                label
                title
                subtitle
              }
            }
            variant
          }
        }
        medianRent {
          title
          subtitle
          disclaimer
          cost {
            title
            suffix
          }
          costChange {
            title
            value
            iconNameV2
            variant
          }
          graph {
            points {
              date
              value
              hover {
                label
                title
                subtitle
              }
            }
            variant
          }
        }
        supplyDemandAndPerformance {
          title
          subtitle {
            ...DynamicContent
          }
          graph {
            dataPoints {
              date
              demand
              supply
              hover {
                label
                description {
                  subtitle
                  icon
                  value
                }
              }
            }
            supplyVariant
            demandVariant
          }
        }
      }
    }
  `,
  [dynamicContentFragment],
);

const propertyPageMarketTrendsBrickFragment = graphql(
  `
    fragment PropertyPage_MarketTrendsBrick on PropertyPage_Page {
      marketTrends {
        title
        subtitle
        comparableSoldProperties {
          description
          factsHeader
          showMoreButton {
            title
          }
          items {
            visible {
              ...PropertyPage_MarketTrends_PropertyAccordionItem
            }
            hidden {
              ...PropertyPage_MarketTrends_PropertyAccordionItem
            }
          }
        }
        otherSimilarSoldProperties {
          ...PropertyPage_PropertyCollectionV2
        }
      }
    }

    fragment PropertyPage_MarketTrends_PropertyAccordionItem on ComparableSoldProperties_AccordionItem {
      isDefaultOpen
      tracking {
        expand {
          schema
          data {
            event_name
            in_page_source
            user_action
            accordion_copy
          }
        }
        collapse {
          schema
          data {
            event_name
            in_page_source
            user_action
            accordion_copy
          }
        }
      }
      property {
        address
        soldDate
        propertyFacts {
          title
          subtitle
        }
        features {
          iconNameV2
          title
          hoverTitle
          valueWithUnit
        }
        image {
          alt
          mobileJpgUri: uri(width: 334, height: 198)
          mobileWebpUri: uri(width: 334, height: 198, format: WEBP)
          desktopJpgUri: uri(width: 88, height: 56)
          desktopWebpUri: uri(width: 88, height: 56, format: WEBP)
        }
        weblinks {
          key
          title
          url
          isExternal
          variant
          iconInfo {
            name
            placement
          }
          tracking {
            clickThroughTracking {
              pathname
              page
              element
              sourceType
            }
          }
          rel
        }
        badge {
          text
          variant
        }
      }
    }
  `,
  [propertyPagePropertyCollectionV2Fragment],
);

const propertyPageAdvertisementBrickFragment = graphql(`
  fragment PropertyPage_AdvertisementBrick on PropertyPage_Page {
    advertisement {
      key
      variant
    }
  }
`);

const propertyPageTrackingFragment = graphql(`
  fragment PropertyPage_Tracking on PropertyPage_Page {
    tracking {
      pageData {
        pageType
        siteSection
        site
        siteSubSection
      }
      useUserContext
      agentsContext {
        schema
        data {
          agents {
            agent_id
            agency_id
          }
        }
      }
      propertyContext @skip(if: $togglePropertyReport) {
        schema
        data {
          property_id
          listing_id
          active_rea_listing
          country
          state
          suburb
          postcode
          bedrooms
          bathrooms
          car_spaces
          year_built
          property_type
          land_size_sq_metres
          floor_area
          avm_estimated_value @include(if: $toggleShowAVMData)
          avm_low_range
          avm_high_range
          avm_confidence
          avm_last_updated_date
          tracked_relationship_typeV2
        }
      }
      propertyContextV2 @include(if: $togglePropertyReport) {
        schema
        data {
          property_id
          listing_id
          active_rea_listing
          country
          state
          suburb
          postcode
          bedrooms
          bathrooms
          car_spaces
          year_built
          property_type
          land_size_sq_metres
          floor_area
          avm_last_updated_date
          tracked_relationship_typeV2
          property_report
        }
      }
    }
  }
`);

export const propertyPagePageHeaderFragment = graphql(
  `
    fragment PropertyPage_Header on PropertyPage_Page {
      header {
        status {
          badgeV2 {
            text
            variant
          }
          tooltip
        }
        pageTitleByPart {
          part1
          part2
        }
        propertyFeatures {
          items {
            ...PropertyPage_PropertyFeatureItem
          }
          updatePropertyDetailsCta {
            ... on PropertyPage_UpdatePropertyDetailsModalCta {
              button {
                ...Button
              }
              openModalHash
              modalEntryPoint
            }
            ... on PropertyPage_SignUpToUpdatePropertyDetailsCta {
              link {
                ...WebLink
              }
            }
          }
        }
      }
    }

    fragment PropertyPage_PropertyFeatureItem on PropertyPage_PropertyFeatureItem {
      iconNameV2
      title
      valueWithUnit
      hoverTitle
    }
  `,
  [weblinkFragment, buttonFragment],
);
export const pcaChatContextFragment = graphql(`
  fragment PropertyPage_PcaChatContext on PropertyPage_PcaChatContext {
    propertyId
    address
    propertyType
    propertyStatus
    propertyAttributes {
      type
      value
    }
    mainImage {
      ... on PropertyPage_ListingImage {
        mobileFullScreenImageJpgUri: uri(width: 600, height: 450, imageMode: Fit, progressive: true)
      }
      ... on PropertyPage_AerialImage {
        uri(width: FULL)
      }
    }
  }
`);

const propertyPageErrorPageFragment = graphql(`
  fragment ErrorPage on ErrorPage {
    errorCode
  }
`);

export const propertyPagePropertyPageFragment = graphql(
  `
    fragment PropertyPage_Page on PropertyPage_Page {
      id
      ...PropertyPage_Tracking
      breadCrumbs {
        title
        url
      }
      ...PropertyPage_Header
      adTargeting {
        bath
        bed
        cars
        pcode
        proptype
        state
        suburb
        price
      }
      ...PropertyPage_BeforeYouBuyModal
      ...PropertyPage_Helmet
      ...PropertyPage_MediaShowcaseV2
      ...PropertyPage_MediaGalleryV2
      ...PropertyPage_ValueEstimatesV2
      ...PropertyPage_PropertyDescription
      ...PropertyPage_Timeline
      ...PropertyPage_PropertyCollectionV3
      ...PropertyPage_SchoolCollection
      ...PropertyPage_ChildcareCollection
      ...PropertyPage_PropertyFeaturesBrick
      ...PropertyPage_PropertyMap
      ...PropertyPage_InternetAvailabilityBrick
      ...PropertyPage_InternetAvailabilityBrickV2
      ...PropertyPage_GovernmentDataBrick
      ...PropertyPage_GovernmentOverlaysBrick
      ...PropertyPage_NearbyPlanningPermitsBrick
      ...PropertyPage_MarketInsightsBrickV3
      ...PropertyPage_MarketTrendsBrick
      ...PropertyPage_AdvertisementBrick
      pcaChatContext @include(if: $togglePcaChat) {
        ...PropertyPage_PcaChatContext
      }
      neighbouringPropertiesV2 {
        ...PropertyPage_PropertyCollectionV2
      }
      followPropertyModalV2 {
        ...FollowPropertyModal
      }
      updatePropertyDetailsModal {
        subtitle
        link {
          ...WebLink
        }
      }
      vgDisclaimerHtml
    }
  `,
  [
    followPropertyModalFragment,
    beforeYouBuyModalFragment,
    propertyPageHelmetFragment,
    propertyPageMediaShowcaseV2Fragment,
    propertyPageMediaGalleryV2Fragment,
    propertyPageValueEstimatesV2Fragment,
    propertyPagePropertyDescriptionFragment,
    propertyPageTimelineV4Fragment,
    propertyPagePropertyCollectionV3Fragment,
    propertyPagePropertyCollectionV2Fragment,
    propertyPageSchoolCollectionFragment,
    propertyPageChildcareCollectionFragment,
    propertyPagePropertyFeaturesBrickFragment,
    propertyPagePropertyMap,
    propertyPageInternetAvailabilityBrickFragment,
    propertyPageInternetAvailabilityBrickV2Fragment,
    propertyPageGovernmentDataBrickFragment,
    propertyPageGovernmentOverlaysBrickFragment,
    propertyPageNearbyPlanningPermitsBrickFragment,
    propertyPageMarketInsightsBrickV3Fragment,
    propertyPageMarketTrendsBrickFragment,
    propertyPageAdvertisementBrickFragment,
    propertyPagePageHeaderFragment,
    pcaChatContextFragment,
    propertyPageTrackingFragment,
    dynamicContentFragment,
    weblinkFragment,
  ],
);

export const propertyPageQuery = graphql(
  `
    query PropertyPageBySlug(
      $id: ID!
      $slug: Uri!
      $toggleShowAVMData: Boolean = false
      $toggleShowOriginNbnBrick: Boolean = true
      $toggleMainImageDataMigration: Boolean = false
      $togglePropertyReport: Boolean = false
      $toggleBYBLegalDisclaimers: Boolean = false
      $togglePcaChat: Boolean = false
      $toggleGovernmentOverlaysUpdate: Boolean = false
      $toggleAllianzRenewalBannerV2: Boolean = false
    ) {
      propertyPageBySlugV3(id: $id, slug: $slug) {
        ... on Redirect {
          __typename
          redirectPath
        }
        ... on ErrorPage {
          __typename
          ...ErrorPage
        }
        ... on PropertyPage_Page {
          __typename
          ...PropertyPage_Page
        }
      }
    }
  `,
  [propertyPagePropertyPageFragment, propertyPageErrorPageFragment],
);
